import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import 'react-quill/dist/quill.snow.css';
import { useTranslation } from '../../../redux/TranslationHelper';
import { RichEditorProps } from './RichEditor.types';
import { adaptRichEditorProps, isValidHTML, extractHtmlContent } from './RichEditor.adapter';
import { generateScopeId, applyScopedCSS, sanitizeCSS } from '../../../utils/cssScoper';
import './RichEditor.css';

// Redux state 타입 정의
interface EditorState {
  components?: Array<{
    id: string;
    content: string;
    [key: string]: any;
  }>;
}

interface RootState {
  editor?: EditorState;
}

// jQuery와 magnific-popup 라이브러리 동적 로드 함수
const loadMagnificPopupLibraries = async (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // jQuery가 이미 로드되어 있는지 확인
    const windowWithJQuery = window as any;
    if (windowWithJQuery.jQuery && windowWithJQuery.jQuery.fn.magnificPopup) {
      resolve();
      return;
    }

    // jQuery 로드
    if (!windowWithJQuery.jQuery) {
      const jqueryScript = document.createElement('script');
      jqueryScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.6.0/jquery.min.js';
      jqueryScript.onload = () => {
        // jQuery 로드 후 magnific-popup 로드
        loadMagnificPopup(resolve, reject);
      };
      jqueryScript.onerror = () => reject(new Error('jQuery 로드 실패'));
      document.head.appendChild(jqueryScript);
    } else {
      // jQuery가 있으면 바로 magnific-popup 로드
      loadMagnificPopup(resolve, reject);
    }
  });
};

const loadMagnificPopup = (resolve: () => void, reject: (error: Error) => void) => {
  // CSS 로드
  if (!document.querySelector('link[href*="magnific-popup"]')) {
    const cssLink = document.createElement('link');
    cssLink.rel = 'stylesheet';
    cssLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/magnific-popup.js/1.1.0/magnific-popup.min.css';
    document.head.appendChild(cssLink);
  }

  // JS 로드
  const script = document.createElement('script');
  script.src = 'https://cdnjs.cloudflare.com/ajax/libs/magnific-popup.js/1.1.0/jquery.magnific-popup.min.js';
  script.onload = () => resolve();
  script.onerror = () => reject(new Error('Magnific Popup 로드 실패'));
  document.head.appendChild(script);
};

/**
 * 리치 에디터 컴포넌트
 */
const RichEditorComponent: React.FC<RichEditorProps> = (props) => {
  // 어댑터를 통해 props 변환
  const adaptedProps = useMemo(() => adaptRichEditorProps(props), [props]);
  const {
    id,
    content,
    style = {},
    className = '',
    isPreview = false,
    componentProps = {}
  } = adaptedProps;

  const { t } = useTranslation();
  const [editorContent, setEditorContent] = useState<string>('');
  const [scopeId] = useState(() => generateScopeId('richeditor'));
  const contentRef = useRef<HTMLDivElement>(null);
  
  // Redux에서 컴포넌트 찾기 (에디터 모드에서 사용)
  const component = useSelector((state: RootState) => 
    state.editor?.components?.find((comp) => comp.id === id)
  );

  // 컴포넌트 클래스 이름 설정
  const componentClassName = useMemo(() => {
    const baseClass = 'rich-editor-component';
    const previewClass = isPreview ? 'preview-mode' : '';
    const customClass = className || '';
    return `${baseClass} ${previewClass} ${customClass}`.trim();
  }, [isPreview, className]);

  // 콘텐츠 변경 감지 및 적용
  useEffect(() => {
    let contentToSet = '';
    
    // 컴포넌트 데이터가 있는 경우 (에디터 모드)
    if (component && component.content) {
      contentToSet = extractHtmlContent(component.content);
    } 
    // props에서 직접 content를 받은 경우 (프리뷰 모드)
    else if (content) {
      contentToSet = extractHtmlContent(content);
    }
    // componentProps에서 content를 받은 경우 (에디터 컴포넌트가 사용하는 경우)
    else if (componentProps && componentProps.content) {
      contentToSet = extractHtmlContent(componentProps.content);
    }
    
    setEditorContent(contentToSet);
  }, [component, content, componentProps]);

  // HTML 내용 검증 함수 메모이제이션
  const isValid = useMemo(() => isValidHTML(editorContent), [editorContent]);

  // CSS 스타일 처리
  useEffect(() => {
    if (!editorContent || !isValid) return;

    // HTML에서 style 태그 추출
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    const styleMatches = editorContent.match(styleRegex);
    
    if (styleMatches) {
      // 모든 style 태그의 내용을 추출
      const styles = styleMatches.map(match => {
        const content = match.replace(/<\/?style[^>]*>/gi, '');
        return content.trim();
      }).filter(s => s).join('\n\n');
      
      if (styles) {
        console.log('[RichEditor] 추출된 CSS:', styles);
        console.log('[RichEditor] 컴포넌트 스코프 ID:', scopeId);
        
        // CSS 정제 (보안)
        const sanitizedCSS = sanitizeCSS(styles);
        
        // 스코프가 적용된 CSS를 head에 추가
        applyScopedCSS(sanitizedCSS, scopeId, `richeditor-style-${id}`, {
          preserveRootVars: true,  // :root의 CSS 변수 보존
          scopeKeyframes: true     // keyframes 이름에 스코프 추가
        });
        console.log('[RichEditor] CSS 스코프 적용 완료');
      }
    }
    
    // 컴포넌트 언마운트 시 스타일 제거
    return () => {
      const styleElement = document.getElementById(`richeditor-style-${id}`);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [editorContent, isValid, id, scopeId]);
  
  // HTML 내용 추출 메모이제이션
  const htmlContent = useMemo(() => {
    const extracted = extractHtmlContent(editorContent);
    // style 태그는 제거하지 않음 - CSS 스코핑으로 격리됨
    return extracted;
  }, [editorContent]);

  // magnific-popup 초기화
  useEffect(() => {
    const initializeMagnificPopup = async () => {
      // 유효한 HTML 콘텐츠가 있고, popup-image 클래스가 포함된 경우에만 초기화
      if (!isValid || !htmlContent.includes('popup-image')) {
        return;
      }

      try {
        // magnific-popup 라이브러리 로드
        await loadMagnificPopupLibraries();
        
        // 잠시 대기 (DOM 렌더링 완료 후)
        setTimeout(() => {
          const windowWithJQuery = window as any;
          if (windowWithJQuery.jQuery && contentRef.current) {
            const $ = windowWithJQuery.jQuery;
            
            // 현재 컴포넌트 내의 popup-image 요소들만 선택
            const popupImages = $(contentRef.current).find('.popup-image');
            
            if (popupImages.length > 0) {
              popupImages.magnificPopup({
                type: 'image',
                gallery: {
                  enabled: true,
                  navigateByImgClick: true,
                  preload: [0, 1]
                },
                image: {
                  titleSrc: function(item: any) {
                    return item.el.find('img').attr('alt') || '';
                  }
                },
                callbacks: {
                  elementParse: function(item: any) {
                    // 이미지 URL이 올바른지 확인
                    item.src = item.el.attr('href');
                  }
                }
              });
              
              console.log(`[RichEditor] Magnific Popup initialized for ${popupImages.length} images`);
            }
          }
        }, 100);
        
      } catch (error) {
        console.error('[RichEditor] Magnific Popup 초기화 실패:', error);
      }
    };

    initializeMagnificPopup();
    
    // 클린업 함수
    return () => {
      const windowWithJQuery = window as any;
      if (windowWithJQuery.jQuery && contentRef.current) {
        const $ = windowWithJQuery.jQuery;
        $(contentRef.current).find('.popup-image').each(function(this: any) {
          if ($(this).data('magnificPopup')) {
            $(this).magnificPopup('destroy');
          }
        });
      }
    };
  }, [isValid, htmlContent]);

  return (
    <div className={componentClassName} style={style} data-editor-scope={scopeId}>
      {isValid ? (
        <div 
          ref={contentRef}
          className="rich-editor-display"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      ) : (
        <div className="rich-editor-error">
          {editorContent ? t('유효하지 않은 HTML 내용입니다. HTML 코드를 확인해주세요.') : t('내용을 입력해주세요.')}
        </div>
      )}
    </div>
  );
};

export default RichEditorComponent; 